import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminComponentPriceService } from "@/services/admin-component-price.service";
import type {
  ComponentPriceFilters,
  ComponentPriceDetailDto,
  CreateComponentPriceRequest,
  UpdateComponentPriceRequest,
} from "@/types/component-price";

/**
 * Query key factory for component prices
 */
export const componentPriceKeys = {
  all: ["componentPrices"] as const,
  lists: () => [...componentPriceKeys.all, "list"] as const,
  list: (componentId: string, filters: ComponentPriceFilters) =>
    [...componentPriceKeys.lists(), componentId, filters] as const,
  details: () => [...componentPriceKeys.all, "detail"] as const,
  detail: (id: string) => [...componentPriceKeys.details(), id] as const,
};

/**
 * Hook to fetch paginated list of component prices
 *
 * @param componentId - Pricing component ID to fetch prices for
 * @param filters - Filter parameters for price list
 * @returns Query result with price list and pagination
 */
export function useComponentPrices(
  componentId: string,
  filters: ComponentPriceFilters,
) {
  return useQuery({
    queryKey: componentPriceKeys.list(componentId, filters),
    queryFn: () =>
      adminComponentPriceService.getComponentPrices(componentId, filters),
    enabled: !!componentId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
  });
}

/**
 * Hook to fetch single component price by ID
 *
 * @param priceId - Price ID to fetch
 * @param enabled - Whether the query should run (default: true if priceId exists)
 * @returns Query result with price details
 */
export function useComponentPrice(
  priceId: string | undefined,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: componentPriceKeys.detail(priceId!),
    queryFn: () => adminComponentPriceService.getComponentPriceById(priceId!),
    enabled: !!priceId && enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to create new component price
 *
 * Automatically invalidates component price list queries on success.
 *
 * @returns Mutation function and state
 */
export function useCreateComponentPrice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      componentId,
      data,
    }: {
      componentId: string;
      data: CreateComponentPriceRequest;
    }) => adminComponentPriceService.createComponentPrice(componentId, data),
    onSuccess: (createdPrice) => {
      // Invalidate all price list queries for this component
      queryClient.invalidateQueries({ queryKey: componentPriceKeys.lists() });

      // Optionally set the created price in cache
      queryClient.setQueryData(
        componentPriceKeys.detail(createdPrice.id),
        createdPrice,
      );

      toast.success("Component price created successfully", {
        description: `Price for ${createdPrice.componentName} has been created.`,
      });
    },
    onError: (error: Error) => {
      toast.error("Failed to create component price", {
        description: error.message || "An unexpected error occurred",
      });
    },
  });
}

/**
 * Hook to update existing component price
 *
 * Automatically invalidates component price queries on success.
 *
 * @returns Mutation function and state
 */
export function useUpdateComponentPrice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateComponentPriceRequest;
    }) => adminComponentPriceService.updateComponentPrice(id, data),
    onSuccess: (updatedPrice: ComponentPriceDetailDto, variables) => {
      // Update cache with returned data
      queryClient.setQueryData(
        componentPriceKeys.detail(variables.id),
        updatedPrice,
      );

      // Invalidate all price list queries
      queryClient.invalidateQueries({ queryKey: componentPriceKeys.lists() });

      toast.success("Component price updated successfully", {
        description: "The price has been updated.",
      });
    },
    onError: (error: Error) => {
      toast.error("Failed to update component price", {
        description: error.message || "An unexpected error occurred",
      });
    },
  });
}

/**
 * Hook to delete component price
 *
 * Automatically invalidates component price queries on success.
 *
 * @returns Mutation function and state
 */
export function useDeleteComponentPrice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (priceId: string) =>
      adminComponentPriceService.deleteComponentPrice(priceId),
    onSuccess: (_, priceId) => {
      // Remove the price from cache
      queryClient.removeQueries({
        queryKey: componentPriceKeys.detail(priceId),
      });

      // Invalidate all price list queries
      queryClient.invalidateQueries({ queryKey: componentPriceKeys.lists() });

      toast.success("Component price deleted successfully", {
        description: "The price has been permanently deleted.",
      });
    },
    onError: (error: Error) => {
      toast.error("Failed to delete component price", {
        description: error.message || "An unexpected error occurred",
      });
    },
  });
}
